// src/components/admin/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Select,
  Stack,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import axios from 'axios';

const Dashboard = () => {
  const [timeframe, setTimeframe] = useState('24h');
  const [stats, setStats] = useState(null);
  const [searchTrends, setSearchTrends] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/matching-stats?timeframe=${timeframe}`
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [timeframe]);

  const fetchSearchTrends = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/search-trends`
      );
      setSearchTrends(response.data);
    } catch (error) {
      console.error('Error fetching search trends:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSearchTrends();
  }, [fetchStats, fetchSearchTrends]);

  return (
    <Box>
      <Stack spacing={6}>
        <Box>
          <Heading size="lg" mb={6}>ダッシュボード</Heading>
          <Select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            width="200px"
            mb={4}
          >
            <option value="24h">過去24時間</option>
            <option value="7d">過去7日間</option>
            <option value="30d">過去30日間</option>
          </Select>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <StatCard
            title="総マッチング回数"
            value={stats?.totalMatches || 0}
            unit="回"
          />
          <StatCard
            title="平均マッチングスコア"
            value={stats?.averageScore ? stats.averageScore.toFixed(1) : 0}
            unit="ポイント"
          />
          <StatCard
            title="高マッチング率商品"
            value={stats?.highMatchCount || 0}
            unit="件"
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <TimeSeriesChart data={stats?.hourlyStats} />
          <TrendsChart data={searchTrends} />
        </SimpleGrid>
      </Stack>
    </Box>
  );
};

const StatCard = ({ title, value, unit }) => (
  <Card>
    <CardHeader>
      <Heading size="sm">{title}</Heading>
    </CardHeader>
    <CardBody>
      <Stat>
        <StatNumber>{value}</StatNumber>
        <StatHelpText>{unit}</StatHelpText>
      </Stat>
    </CardBody>
  </Card>
);

const TimeSeriesChart = ({ data }) => (
  <Card>
    <CardHeader>
      <Heading size="sm">時間帯別利用状況</Heading>
    </CardHeader>
    <CardBody>
      <Box height="300px" width="100%" minH="300px">
        <ResponsiveContainer>
          <LineChart data={data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="_id" 
              label={{ value: '時間', position: 'bottom' }}
            />
            <YAxis 
              label={{ value: '利用回数', angle: -90, position: 'left' }}
            />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey="count" 
              stroke="#8884d8" 
              name="利用回数"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </CardBody>
  </Card>
);

const TrendsChart = ({ data }) => (
  <Card>
    <CardHeader>
      <Heading size="sm">検索条件の傾向</Heading>
    </CardHeader>
    <CardBody>
      <Box height="300px" width="100%" minH="300px">
        <ResponsiveContainer>
          <BarChart data={data?.colors || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              label={{ value: '条件', position: 'bottom' }}
            />
            <YAxis 
              label={{ value: '検索回数', angle: -90, position: 'left' }}
            />
            <Tooltip />
            <Bar 
              dataKey="value" 
              fill="#8884d8" 
              name="検索回数"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </CardBody>
  </Card>
);

export default Dashboard;